import { initPreferences, updatePreferences } from '@vben/preferences';

import { overridesPreferences, preferencesExtension } from './preferences';

/**
 * 移除并销毁loading（本地实现，不从 @vben/utils 导入）
 *
 * 为什么本地实现：生产构建（rolldown-vite）时，入口文件从 '@vben/utils' barrel 导入
 * 会把整个 utils 包拖进依赖图，rolldown 分块后形成「入口 chunk ↔ utils chunk」循环依赖
 * （utils chunk 顶层会同步调用从入口 chunk 导出的 @babel/runtime helper，加载时该绑定
 * 尚未初始化，运行时报 "X is not a function"）。这里直接内联该函数，切断入口对
 * @vben/utils barrel 的静态依赖。如需改动逻辑，请同步更新
 * packages/utils/src/helpers/unmount-global-loading.ts。
 */
function unmountGlobalLoading() {
  // 查找全局 loading 元素
  const loadingElement = document.querySelector('#__app-loading__');

  if (loadingElement) {
    // 添加隐藏类，触发过渡动画
    loadingElement.classList.add('hidden');

    // 查找所有需要移除的注入 loading 元素
    const injectLoadingElements = document.querySelectorAll(
      '[data-app-loading^="inject"]',
    );

    // 当过渡动画结束时，移除 loading 元素和所有注入的 loading 元素
    loadingElement.addEventListener(
      'transitionend',
      () => {
        loadingElement.remove(); // 移除 loading 元素
        injectLoadingElements.forEach((el) => el.remove()); // 移除所有注入的 loading 元素
      },
      { once: true },
    ); // 确保事件只触发一次
  }
}

/**
 * 应用初始化完成之后再进行页面加载渲染
 */
async function initApplication() {
  // name用于指定项目唯一标识
  // 用于区分不同项目的偏好设置以及存储数据的key前缀以及其他一些需要隔离的数据
  const env = import.meta.env.PROD ? 'prod' : 'dev';
  const appVersion = import.meta.env.VITE_APP_VERSION;
  const namespace = `${import.meta.env.VITE_APP_NAMESPACE}-${appVersion}-${env}`;

  // app偏好设置初始化
  await initPreferences({
    extension: preferencesExtension,
    namespace,
    overrides: overridesPreferences,
  });

  // 强制回写 Logo / 检查更新相关配置
  // initPreferences 的合并顺序是「用户缓存 > overridesPreferences」，历史缓存里的旧值会压过
  // preferences.ts 的配置，导致改了源码却不生效。已知两类：
  //   1) 缓存里的 logo.showText=false / logo.logoMode='full' / app.name=''
  //      会让菜单栏顶部只剩 Logo、系统名称不显示；
  //   2) 缓存里的 app.enableCheckUpdates=true（vben 默认值）会让
  //      「点击刷新以获取最新版本」弹框反复出现（每 checkUpdatesInterval 分钟轮询一次，
  //      生产构建后 index.html 的 etag 变化即触发）。
  // 这里统一回写一次，保证 preferences.ts 的配置一定生效，无需用户手动清缓存。
  updatePreferences({
    app: {
      enableCheckUpdates:
        overridesPreferences.app?.enableCheckUpdates ?? false,
      name: overridesPreferences.app?.name ?? import.meta.env.VITE_APP_TITLE,
    },
    logo: {
      logoMode: 'icon',
      showText: overridesPreferences.logo?.showText ?? true,
    },
  });

  // 启动应用并挂载
  // vue应用主要逻辑及视图
  const { bootstrap } = await import('./bootstrap');
  await bootstrap(namespace);

  // 移除并销毁loading
  unmountGlobalLoading();
}

initApplication();
